import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFormContext, useWatch } from "react-hook-form";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";

import Field from ".";
import { getDatagridFormMethods } from "../services/form.service";
import { methodRefs } from "../context/formSlice";
import { useDatagridContextValue } from "../context/datagridContext";
import { useReviewPageCheck } from "../context/reviewItemContext";
import useUpdatePanelHeight from "../hooks/useUpdatePanelHeight";
import { selectFormMiscData } from "../../../selectors";

function FormDynamicComponent(props) {
  const { fieldProps } = props;

  const { customConfig } = fieldProps;
  const [renderedFields, setRenderedFields] = useState([]);
  const {
    control: formControl,
    setValue: formSetValue,
    isDialogForm,
  } = useFormContext();
  const formMisc = useSelector((state) =>
    selectFormMiscData(state, isDialogForm)
  );
  const isReviewContext = useReviewPageCheck();
  const { datagridFormState } = useDatagridContextValue();
  const formMethods = getDatagridFormMethods(
    methodRefs,
    datagridFormState,
    isReviewContext
  );
  const { updateHeight } = useUpdatePanelHeight();
  const setValue = datagridFormState ? formMethods.setValue : formSetValue;
  const control = datagridFormState ? formMethods.control : formControl;

  const { sourceKey, fields, dynamicFieldPrefix } = customConfig || {};
  // Note: source field must be of type multiselect
  const sourceFieldValue = useWatch({
    name: sourceKey,
    control,
    disabled: !sourceKey,
  });

  const getSourceFieldValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value) && typeof value.filter(Boolean)[0] === "string")
      return value;
    return [];
  };

  const getMappingLabel = (selectedValue) => {
    const mapping = formMisc?.[sourceKey];
    if (mapping && Array.isArray(mapping)) {
      const mappedItem = mapping.find((item) => item.value === selectedValue);
      return mappedItem ? mappedItem.label : selectedValue;
    }
    return selectedValue;
  };

  useEffect(() => {
    const _sourceFieldValue = Array.isArray(sourceFieldValue)
      ? sourceFieldValue.filter((value) => value && value !== "NA")
      : sourceFieldValue;
    if (!customConfig || !_sourceFieldValue) {
      if (renderedFields.length) {
        // if the source field value is cleared, then remove & reset all the rendered fields
        renderedFields.forEach((rf) => setValue(rf.key, ""));
        setRenderedFields([]);
      }
      return;
    }
    const selectedValues = getSourceFieldValue(_sourceFieldValue);
    const fieldsToRender = [];
    selectedValues.forEach((selectedValue) => {
      fields.forEach((field) => {
        const label = getMappingLabel(selectedValue);
        const fieldConfigs = {
          ...field,
          label: label ? `${label}'s ${field.label}` : field.label,
          key: `${dynamicFieldPrefix}_${selectedValue}`,
        };
        fieldsToRender.push(fieldConfigs);
      });
    });

    const removedFields = renderedFields.filter(
      (rf) => !fieldsToRender.find(({ key }) => key && key === rf.key)
    );
    setRenderedFields(fieldsToRender);
    setTimeout(() => removedFields.forEach((rf) => setValue(rf.key, "")), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFieldValue, formMisc?.[sourceKey]]);

  useEffect(() => {
    updateHeight();
    setTimeout(() => updateHeight(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedFields.length]);

  if (!customConfig || !renderedFields.length) return null;
  return (
    <Grid container columnSpacing={3}>
      {renderedFields.map((fieldConfig) => (
        <Grid
          item
          key={fieldConfig.key + fieldConfig.label}
          xs={12}
          sm={6}
          md={4}
        >
          <Field fieldProps={fieldConfig} />
        </Grid>
      ))}
    </Grid>
  );
}

FormDynamicComponent.propTypes = {
  fieldProps: PropTypes.shape({
    type: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    validate: PropTypes.shape({
      required: PropTypes.bool,
      custom: PropTypes.string,
    }),
    customConfig: PropTypes.shape({
      sourceKey: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
  }).isRequired,
};

export default FormDynamicComponent;
